import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Modal } from "antd";
import Close from "../../../../resources/imgs/modal/close.svg";
import { FaChevronRight } from "react-icons/fa";
import { useRecoilValue } from 'recoil'
import { assessmentAtom } from "../../../../state/assessment";
import { addAssessment } from '../../../../services/assessment';
import Progress from "../../progress";
import HelpBtn from "../../../../resources/imgs/assessment/helpBtn.svg"

// Audio file paths for button sounds
// Use PUBLIC_URL for correct path in production
const basePath = process.env.PUBLIC_URL || '';
const BUTTON_SOUNDS = {
  1: `${basePath}/sounds/module3/button1.wav`,
  2: `${basePath}/sounds/module3/button2.wav`, 
  3: `${basePath}/sounds/module3/button3.wav`,
  4: `${basePath}/sounds/module3/button4.wav`
};

// This will be dynamically created based on instance data

var initialVal = {
  meal: "Steak",
  expectedSequence: [1, 2, 3],
  userSequence: [1, 2, 3],
  isCorrect: true
}

export default function Index(props) {
  // Use instance data from adaptive system or fall back to hardcoded
  const instanceData = props.instanceData || {
    difficulty: 3,
    mealOrder: ["Steak", "Salmon", "Shrimp"],
    buttonSequence: [[1, 2, 3], [2, 3, 4], [3, 4, 1]]
  };

  // Create initial results state that persists across re-renders
  const [initialRes, setInitialRes] = useState({
    module: 3,
    instanceId: instanceData.instanceId || 3,
    difficulty: instanceData.difficulty,
    moduleKey: "order_entry",
    isCompleted: true,
    mode: instanceData.requireMemory ? "memorize-and-reproduce" : "observe-and-reproduce",
    sequenceShownOrder: instanceData.mealOrder,
    sequenceInputOrder: instanceData.mealOrder,
    attempts: []
  });
  let timer;
  const assessment = useRecoilValue(assessmentAtom);
  const [processIndex, setProcessIndex] = useState(0);
  const [counter, setCounter] = useState(0);
  const [flag, setFlag] = useState(0);
  const [isShowing, setIsShowing] = useState(0);
  const [res, setRes] = useState([0, 0, 0, 0]);
  const [open, setOpen] = useState(false);
  const [userSequence, setUserSequence] = useState([]);
  const intervalRef = useRef(null);
  const audioRefs = useRef({});
  const [hasStarted, setHasStarted] = useState(false);
  // Create dynamic data based on instance data
  const data = useMemo(() => {
    if (instanceData.mealOrder && instanceData.buttonSequence) {
      // Use instance data if available - create entries for each meal in mealOrder
      const instanceMeals = instanceData.mealOrder.map((meal, index) => ({
        dish: meal,
        sequence: instanceData.buttonSequence[index] || [1, 2, 3]
      }));
      
      // Fill remaining slots with hardcoded meals to maintain 6-dish display
      const hardcodedMeals = [
        { dish: "Steak", sequence: [1, 2, 3] },
        { dish: "Salmon", sequence: [2, 3, 4] },
        { dish: "Shrimp", sequence: [3, 4, 1] },
        { dish: "Salad", sequence: [4, 1, 2] },
        { dish: "Chicken", sequence: [1, 3, 2] },
        { dish: "Lamb", sequence: [2, 4, 3] },
      ];
      
      // Replace hardcoded meals with instance meals where they match
      const finalData = [...hardcodedMeals];
      instanceMeals.forEach(instanceMeal => {
        const existingIndex = finalData.findIndex(hardMeal => 
          hardMeal.dish.toLowerCase() === instanceMeal.dish.toLowerCase()
        );
        if (existingIndex !== -1) {
          finalData[existingIndex] = instanceMeal;
        }
      });
      
      return finalData;
    } else {
      // Fallback to hardcoded data
      return [
        { dish: "Steak", sequence: [1, 2, 3] },
        { dish: "Salmon", sequence: [2, 3, 4] },
        { dish: "Shrimp", sequence: [3, 4, 1] },
        { dish: "Salad", sequence: [4, 1, 2] },
        { dish: "Chicken", sequence: [1, 3, 2] },
        { dish: "Lamb", sequence: [2, 4, 3] },
      ];
    }
  }, [instanceData]);

  // Create observed order data for instances 7-8
  const observedOrderData = useMemo(() => {
    if (instanceData.showDifferentOrder && instanceData.observedOrder) {
      // Create data structure based on observedOrder for showing sequence
      return instanceData.observedOrder.map((meal, index) => {
        // Find the meal in the original mapping to get the correct sequence
        const mealToSequenceMap = {
          "Steak": [1, 2, 3],
          "Salmon": [2, 3, 4], 
          "Shrimp": [3, 4, 1],
          "Salad": [4, 1, 2],
          "Chicken": [1, 3, 2],
          "Lamb": [2, 4, 3]
        };
        return {
          dish: meal,
          sequence: mealToSequenceMap[meal] || [1, 2, 3]
        };
      });
    }
    return null;
  }, [instanceData]);

  // Determine current instruction text
  const getInstructionText = () => {
    if (instanceData.requireMemory) {
      // Instances 9-10: Memory only
      return "Reproduce the pattern from memory";
    } else if (isShowing) {
      // User input phase
      // Check if this is instance 7 or 8 (showDifferentOrder is true for these instances)
      if (instanceData.showDifferentOrder) {
        return "Reproduce the pattern. Oh no! The dishes on the machine switched order! Make sure you tap the correct sequence for each highlighted meal";
      } else {
        return "Reproduce the pattern";
      }
    } else if (hasStarted) {
      // Pattern showing phase after start clicked - show only first sentence
      return "Watch the pattern closely so you can replicate it";
    } else {
      // Pattern showing phase before start clicked - include both sentences
      return "Watch the pattern closely so you can replicate it. Press start when you're ready to observe the pattern.";
    }
  };

  // Determine if sequence should be shown (skip for instances 9-10)
  const shouldShowSequence = instanceData.showSequence !== false;

  // Get the data to use for sequence display (observedOrder for 7-8, regular order for others)
  const sequenceDisplayData = observedOrderData || data;

  // Get the order for user input (mealOrder for 7-8 which is different from observedOrder)
  const userInputOrder = instanceData.mealOrder || instanceData.observedOrder || data.map(d => d.dish);
  
  // Initialize audio objects
  useEffect(() => {
    console.log('🎵 Module3_3: PUBLIC_URL =', process.env.PUBLIC_URL);
    console.log('🎵 Module3_3: basePath =', basePath);
    console.log('🎵 Module3_3: Window location =', window.location.hostname);
    console.log('🎵 Module3_3: Full URLs being used:', BUTTON_SOUNDS);
    
    // Create audio objects for each button
    Object.keys(BUTTON_SOUNDS).forEach(buttonNum => {
      const fullUrl = window.location.origin + BUTTON_SOUNDS[buttonNum];
      console.log(`🔊 Module3_3: Loading audio for button ${buttonNum}`);
      console.log(`   Expected URL: ${fullUrl}`);
      
      const audio = new Audio(BUTTON_SOUNDS[buttonNum]);
      audio.preload = 'auto';
      audio.volume = 0.7; // Set volume to 70%
      audioRefs.current[buttonNum] = audio;
      
      // Add load event listeners for debugging
      audio.addEventListener('loadeddata', () => {
        console.log(`✅ Module3_3: Audio loaded successfully for button ${buttonNum}`);
      });
      
      audio.addEventListener('error', (e) => {
        console.error(`❌ Module3_3: Audio load error for button ${buttonNum}:`, e);
        console.error(`   Attempted URL: ${BUTTON_SOUNDS[buttonNum]}`);
        console.error(`   Audio error code:`, audio.error);
      });
    });
    
    // Cleanup function
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    };
  }, []);
  
  // Function to play button sound
  const playSound = useCallback((buttonIndex) => {
    console.log(`🔊 Module3_3: Attempting to play sound for button ${buttonIndex}`);
    
    if (audioRefs.current[buttonIndex]) {
      const audio = audioRefs.current[buttonIndex];
      audio.currentTime = 0; // Reset to start
      
      audio.play()
        .then(() => {
          console.log(`✅ Module3_3: Successfully played sound for button ${buttonIndex}`);
        })
        .catch(error => {
          console.error(`❌ Module3_3: Failed to play sound for button ${buttonIndex}:`, error);
          
          // Try to handle user interaction requirement for audio
          if (error.name === 'NotAllowedError') {
            console.log('🔊 Module3_3: Audio blocked - user interaction required first');
          }
        });
    } else {
      console.error(`❌ Module3_3: No audio found for button ${buttonIndex}`);
    }
  }, []);
 
  useEffect(() => {
    setOpen(true);
  }, []);

  // Reset state when instanceData changes (for new instances)
  useEffect(() => {
    console.log('🔄 Module3_3: instanceData changed, resetting state:', instanceData);
    setProcessIndex(0);
    setCounter(0);
    setFlag(0);
    setIsShowing(0);
    setRes([0, 0, 0, 0]);
    setUserSequence([]); // Reset userSequence state
    setHasStarted(false); // Reset hasStarted state
    
    // Reset initialRes with new instance data but preserve structure
    setInitialRes({
      module: 3,
      instanceId: instanceData.instanceId || 3,
      difficulty: instanceData.difficulty,
      moduleKey: "order_entry",
      isCompleted: true,
      mode: instanceData.requireMemory ? "memorize-and-reproduce" : "observe-and-reproduce",
      sequenceShownOrder: instanceData.mealOrder,
      sequenceInputOrder: instanceData.mealOrder,
      attempts: [] // Fresh attempts array for new instance
    });
    
    // Clear any running timers
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [instanceData?.instanceId]); // Only trigger on instance ID changes, not object reference changes

  // Play sound when counter changes during sequence display
  useEffect(() => {
    if (!isShowing && counter >= 2 && counter <= 5 && sequenceDisplayData[processIndex]) {
      const currentMealData = sequenceDisplayData[processIndex];
      if (currentMealData && currentMealData.sequence) {
        const buttonIndex = currentMealData.sequence[counter - 2];
        if (buttonIndex) {
          console.log(`🎵 Module3_3: Playing sound for meal ${currentMealData.dish}, counter=${counter}, button=${buttonIndex}`);
          // Small delay to sync with visual
          setTimeout(() => playSound(buttonIndex), 50);
        }
      }
    }
  }, [counter, processIndex, isShowing, sequenceDisplayData, playSound]);

  // Safety check: stop interval if processIndex exceeds bounds
  useEffect(() => {
    const actualMealCount = (instanceData.mealOrder && instanceData.mealOrder.length) 
      ? instanceData.mealOrder.length 
      : 4; // Default fallback
    const maxProcessIndex = actualMealCount - 1;
    
    console.log(`🛑 Safety bounds check: actualMealCount=${actualMealCount}, maxProcessIndex=${maxProcessIndex}, currentProcessIndex=${processIndex}`);
    
    if (processIndex > maxProcessIndex && intervalRef.current && !isShowing) {
      console.log(`🛑 SAFETY: Stopping interval due to out-of-bounds processIndex: ${processIndex} > ${maxProcessIndex}`);
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setIsShowing(1);
      setProcessIndex(0);
      setCounter(0);
    }
  }, [processIndex, instanceData.mealOrder, data.length, isShowing]);

  const Count = () => {
    console.log(`🔄 Count called: counter=${counter}, processIndex=${processIndex}, isShowing=${isShowing}`);
    
    // Use the actual meal count from instance data for bounds checking
    const actualMealCount = (instanceData.mealOrder && instanceData.mealOrder.length) 
      ? instanceData.mealOrder.length 
      : 4; // Default fallback
    const maxProcessIndex = actualMealCount - 1;
    
    console.log(`🔄 Count bounds check: actualMealCount=${actualMealCount}, maxProcessIndex=${maxProcessIndex}, currentProcessIndex=${processIndex}`);
    
    // Stop immediately if we've shown all meals
    if (processIndex > maxProcessIndex) {
      console.log(`🔄 Already exceeded max meals, stopping. processIndex=${processIndex}, maxProcessIndex=${maxProcessIndex}`);
      setIsShowing(1);
      setProcessIndex(0);
      clearInterval(intervalRef.current);
      return;
    }
    
    setCounter((p) => {
      const newCounter = p + 1;
      if (newCounter >= 7) {
        console.log(`🔄 Counter reached 7: processIndex=${processIndex}, maxProcessIndex=${maxProcessIndex}`);
          
        if (processIndex < maxProcessIndex) {
          console.log(`🔄 Moving to next meal: ${processIndex} -> ${processIndex + 1}`);
          setProcessIndex((prev) => prev + 1);
          return 0; // Reset counter for next meal
        } else {
          console.log(`🔄 All meals shown, allowing user input. Setting isShowing=1`);
          setIsShowing(1);
          setProcessIndex(0);
          clearInterval(intervalRef.current);
          return 0; // Reset counter
        }
      }
      return newCounter;
    });
  }

  const showModal = () => {
    setOpen(true);
  };

  const hideModal = () => {
    setOpen(false);
  };

  const show = (index) => {
    console.log(`👆 Button clicked: index=${index}, button=${index + 1}, isShowing=${isShowing}`);
    
    // Play sound when user clicks button
    playSound(index + 1);
    
    if (isShowing) {
      let sum = res.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
      let arr = [...res];
      arr[index] = 1;
      const newUserSequence = [...userSequence, index + 1];
      setUserSequence(newUserSequence);
      
      console.log(`👆 Button state: sum=${sum}, newUserSequence=${JSON.stringify(newUserSequence)}, res=${JSON.stringify(res)}, arr=${JSON.stringify(arr)}`);

      if (sum == 2) {
        console.log(`🔍 Validation triggered! processIndex=${processIndex}, userInputOrder=${JSON.stringify(userInputOrder)}`);
        
        // Safety check for data availability
        if (!data[processIndex] || !data[processIndex].sequence) {
          console.error(`❌ Invalid data at processIndex ${processIndex}:`, data[processIndex]);
          console.error(`❌ Full data array:`, data);
          return;
        }
        
        // For user input phase, use the meal order specified for reproduction
        const currentMeal = userInputOrder[processIndex];
        console.log(`🔍 Looking for meal: ${currentMeal} at processIndex=${processIndex}`);
        
        const currentMealData = data.find(d => d.dish === currentMeal);
        console.log(`🔍 Found meal data:`, currentMealData);
        
        if (!currentMealData) {
          console.error(`❌ Could not find meal data for: ${currentMeal}`);
          console.error(`❌ Available meals in data:`, data.map(d => d.dish));
          return;
        }
        
        // Check if the user sequence matches the expected sequence
        const expectedSequence = currentMealData.sequence;
        const isCorrect = JSON.stringify(expectedSequence) === JSON.stringify(newUserSequence);
        
        console.log(`🔍 Module3_3 sequence check:`, {
          meal: currentMeal,
          expected: expectedSequence,
          userInput: newUserSequence,
          isCorrect,
          userInputOrder,
          processIndex,
          sum,
          res,
          buttonPressed: index + 1,
          currentMealData,
          instanceDataMealOrder: instanceData.mealOrder,
          instanceDataButtonSequence: instanceData.buttonSequence
        });
        
        initialVal = {
          meal: currentMeal,
          expectedSequence: expectedSequence,
          userSequence: newUserSequence,
          isCorrect: isCorrect
        }

        // Update initialRes state by adding the new attempt
        setInitialRes(prev => ({
          ...prev,
          attempts: [...prev.attempts, initialVal]
        }));
        setUserSequence([]);
        console.log(initialRes)

        // Check completion: processIndex is 0-based, so check if we're at the last meal
        const isLastMeal = processIndex >= userInputOrder.length - 1;
        
        if (isLastMeal) {
          // This is the last meal, set flag to show Next button
          setRes(arr);
          setFlag(1);
          return;
        }

        // Not the last meal yet, continue to next meal
        setRes(arr);
        timer = setTimeout(() => {
          setCounter(0);
          setProcessIndex((p) => p + 1);
          setRes([0, 0, 0, 0]);
        }, 800); // Reduced from 1000ms to 800ms (20% faster)
      } else {
        setRes(arr);
      }
    }
  }

  const nextStep = async () => {
    // If using adaptive system, use the onSubmit callback
    if (props.onSubmit) {
      // Calculate if user got the sequence correct
      const isCorrect = initialRes.attempts.every(attempt => attempt.isCorrect);
      
      console.log(`🎯 Module3_3 submitting to adaptive system:`, {
        isCorrect,
        attempts: initialRes.attempts,
        difficulty: instanceData.difficulty,
        totalAttempts: initialRes.attempts.length,
        correctAttempts: initialRes.attempts.filter(a => a.isCorrect).length
      });
      
      props.onSubmit(isCorrect, {
        attempts: initialRes.attempts,
        difficulty: instanceData.difficulty,
        moduleKey: "order_entry"
      });
    } else {
      // Original behavior for non-adaptive mode
      // Module 3 completed - original mode
      console.log('Module 3 completed', initialRes);
      props.nextPage();
    }
  }

  const start = () => {
    console.log(`🔄 Starting sequence animation. Current state: processIndex=${processIndex}, counter=${counter}, isShowing=${isShowing}`);
    
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Reset state for new sequence and mark as started
    setProcessIndex(0);
    setCounter(0);
    setHasStarted(true);
    
    // For memory-only instances (9-10), skip sequence showing
    if (!shouldShowSequence) {
      console.log('🧠 Memory-only mode: skipping sequence display');
      setIsShowing(1);
      return;
    }
    
    setIsShowing(0);
    intervalRef.current = setInterval(Count, 800); // Reduced from 1000ms to 800ms (20% faster)
    return () => clearInterval(intervalRef.current);
  }

  // Debug logging
  console.log(`🔍 Module3_3 render: processIndex=${processIndex}, data.length=${data.length}, counter=${counter}, isShowing=${isShowing}`);
  console.log(`🔍 Current data:`, data.map((item, idx) => `${idx}: ${item?.dish} [${item?.sequence?.join(',')}]`).join(' | '));

  return (
    <div className="module1-main">
      <div className="module1-wrap">
        <Progress number="03." percent={3 / 8} />
        <div className="module1-content-wrap">
          <div className="module1-logo">Module 3</div>
          <div className="module1-body">
            <div className="module1-title">Entering the guests'orders</div>
            <div className="module1-title">Repeat the pattern that you hear and see</div>
          </div>
        </div>
      </div>

      {!intervalRef.current && !isShowing && (
        <div
          className="assessment-start-btn"
          onClick={() => { start() }}
        >
          Start
        </div>
      )}

      {intervalRef.current && !isShowing && shouldShowSequence && (
        <div className="assessment-status">
          Showing sequence... ({Math.min(processIndex + 1, instanceData.mealOrder?.length || 4)} of {instanceData.mealOrder?.length || 4})
        </div>
      )}

      {/* Dynamic instruction text */}
      <div className="assessment-instruction">
        {getInstructionText()}
      </div>

      <div className="module1-dishes module1-dishes2 module3-dishes">
        {/* Display only the dishes for this specific instance - use actual meal count */}
        {(isShowing ? userInputOrder : sequenceDisplayData.map(d => d.dish)).slice(0, instanceData.mealOrder ? instanceData.mealOrder.length : 4).map((dish, index) => {
          // For sequence display, highlight current dish being shown
          const isCurrentDish = !isShowing && (sequenceDisplayData[processIndex]?.dish === dish);
          // For user input, highlight current dish to input
          const isCurrentInput = isShowing && (userInputOrder[processIndex] === dish);
          
          return (
            <div 
              key={`dish-${index}`}
              className="module1-dish module3-dish hover-none" 
              style={{ 
                backgroundColor: (isCurrentDish || isCurrentInput) ? "#F277C6" : ""
              }}
            >
              {dish}
            </div>
          );
        })}
        {/* Add empty invisible placeholders to maintain visual consistency */}
        {Array.from({ length: Math.max(0, 6 - (instanceData.mealOrder ? instanceData.mealOrder.length : 4)) }, (_, index) => (
          <div 
            key={`placeholder-${index}`} 
            className="module1-dish module3-dish hover-none" 
            style={{ visibility: "hidden" }}
          >
            {/* Empty placeholder */}
          </div>
        ))}
      </div>

      <div className="module1-dishes module1-dishes2 module3-dishes">
        <div className="module1-dish module4-dish1 hover-none" onClick={()=> show(0)} style={{ backgroundColor: ((!isShowing && (sequenceDisplayData[processIndex]?.sequence?.[counter-2] === 1)) || res[0]) ? "#155226" : "white" }}></div>
        <div className="module1-dish module4-dish5 hover-none" onClick={() => show(1)} style={{ backgroundColor: ((!isShowing && (sequenceDisplayData[processIndex]?.sequence?.[counter-2] === 2)) || res[1]) ? "red" : "white" }}></div>
        <div className="module1-dish module4-dish3 hover-none" onClick={() => show(2)} style={{ backgroundColor: ((!isShowing && (sequenceDisplayData[processIndex]?.sequence?.[counter-2] === 3)) || res[2]) ? "#8C7421" : "white" }}></div>
        <div className="module1-dish module4-dish4 hover-none" onClick={() => show(3)} style={{ backgroundColor: ((!isShowing && (sequenceDisplayData[processIndex]?.sequence?.[counter-2] === 4)) || res[3]) ? "blue" : "white" }}></div>
      </div>

      <div className='next-btn-group'>
        <div className='help-btn-group'>
          <div className='help-btn-effect1'></div>
          <div className='help-btn-effect2'></div>
          <img src={HelpBtn} onClick={() => showModal()} />
          <div className='help-content'>Do you need help?</div>
        </div>

        {flag == 1 && 
          <div
            className="assessment-next-btn"
            onClick={() => { nextStep()}}
          >
            Next{" "}
            <div
              style={{
                paddingTop: "5px",
                display: "inline-block",
              }}
            >
              <FaChevronRight color="white" size={16} />
            </div>
          </div>
        }
      </div>

      <Modal
        open={open}
        onOk={() => showModal()}
        onCancel={() => hideModal()}
        closeIcon={<img src={Close}></img>}
        footer={null}
      >
        <div className="direction-modal-container">
          <div className='direction-modal-title'>Directions</div>
          <div className='direction-modal-content'>
            Now that you have finalized the 6 guests’ orders, it’s time to enter their orders into a computer that sends that information to the kitchen.  There’s one problem. The screen of the machine is broken, meaning you’ll have to enter their orders by replicating the pattern of buttons (and associated sounds) that correspond to each meal.
          </div>
        </div>
      </Modal>
    </div>
  )
}
